/**
 * Cost Breakdown v2 — API + shared-config hooks.
 *
 * useCbApi()    -> { get, post, put, del } bound to MSAL auth.
 * useCbConfig() -> the module config served by GET /api/cost-breakdown/config
 *                  (disciplines, sections, column definitions). Cached per session
 *                  so every sheet renders from the same single source of truth.
 */

import { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';

let configCache = null;
let configPromise = null;

export function useCbApi() {
  const { instance, accounts } = useMsal();
  return useMemo(() => ({
    get: (path, opts) => authenticatedApiCall(instance, accounts, 'GET', path, null, opts),
    post: (path, body, opts) => authenticatedApiCall(instance, accounts, 'POST', path, body, opts),
    put: (path, body, opts) => authenticatedApiCall(instance, accounts, 'PUT', path, body, opts),
    del: (path, opts) => authenticatedApiCall(instance, accounts, 'DELETE', path, null, opts),
  }), [instance, accounts]);
}

export function useCbConfig() {
  const api = useCbApi();
  const [config, setConfig] = useState(configCache);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (configCache) return;
    if (!configPromise) {
      configPromise = api.get('/cost-breakdown/config')
        .then((res) => {
          configCache = res.data?.config || null;
          return configCache;
        })
        .catch((err) => {
          configPromise = null;
          throw err;
        });
    }
    let mounted = true;
    configPromise.then(
      (cfg) => { if (mounted) setConfig(cfg); },
      (err) => { if (mounted) setError(err.message); }
    );
    return () => { mounted = false; };
  }, [api]);

  return { config, error, loading: !config && !error };
}

/** Mirror of the backend's calcDetailRow so edits recompute instantly client-side. */
export function calcDetailRow(row) {
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const vulcCost = num(row.vulc_time) * num(row.vulc_rate);
  const totalSub = vulcCost + num(row.survey_sub_cost) + num(row.misc_other_subs_cost);
  const totalMaterial =
    num(row.shims_cost) + num(row.hy_200_cost) + num(row.misc_anchors_cost) +
    num(row.misc_material_cost) + num(row.grout_mat_cost_incl_form);
  const totalHours = num(row.grout_labour_hours) + num(row.labour_hours_a) + num(row.labour_hours_b);
  const factor = row.labour_hours_factor === null || row.labour_hours_factor === undefined || row.labour_hours_factor === ''
    ? 100 : num(row.labour_hours_factor);
  return {
    vulc_cost: Math.round(vulcCost * 100) / 100,
    total_sub_cost: Math.round(totalSub * 100) / 100,
    total_material_cost: Math.round(totalMaterial * 100) / 100,
    total_combined_labour_hours: Math.round(totalHours * 100) / 100,
    factored_total_comb_hours: Math.round(totalHours * (factor / 100) * 100) / 100,
  };
}
